import React, { useContext, useState } from 'react';
import { X } from 'lucide-react';
import type { DateBasedTriggerMetadata } from '@reach/shared-types/business-automation';
import type { AutomationTemplateContents } from '@reach/shared-types/automation-template';
import { AutomationEditorContext } from '../AutomationEditorContext';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import { DateTriggerDateSelectionStep } from './DateTriggerDateSelectionStep';
import { DateTriggerAudienceSelectionStep } from './DateTriggerAudienceSelectionStep';
import { DATE_TRIGGER_STEP_COPY } from './date-trigger-step-copy';
import { getSegmentDefaultIds } from '../segment-picker/segment-usage';
import { getNextExclusions } from './trigger-exclusions';
import styles from './DateTriggerConfigDialog.module.css';

interface DateTriggerConfigDialogProps {
  onClose: () => void;
  triggerMetadata: DateBasedTriggerMetadata;
}

type WizardStep = 'date' | 'audience';
type SegmentDefaults = AutomationTemplateContents['includeSegmentDefaults'];

const WIZARD_STEPS: WizardStep[] = ['date', 'audience'];

function getInitialAudienceId(includeSegmentDefaults: SegmentDefaults | undefined): string {
  const firstDefault = includeSegmentDefaults?.[0];
  if (!firstDefault) return '';
  return typeof firstDefault === 'string' ? firstDefault : firstDefault.id;
}

function getStepperState(index: number, currentIndex: number): 'complete' | 'active' | 'upcoming' {
  if (index < currentIndex) return 'complete';
  if (index === currentIndex) return 'active';
  return 'upcoming';
}

export const DateTriggerConfigDialog: React.FC<DateTriggerConfigDialogProps> = ({
  onClose,
  triggerMetadata,
}) => {
  const { editor } = useContext(AutomationEditorContext)!;
  const { updateTrigger, updateAudience, updateExclusions, state } = editor;
  const { excludeSegmentDefaults } = state.localContents;

  const [step, setStep] = useState<WizardStep>('date');
  const [selectedMonth, setSelectedMonth] = useState<number>(triggerMetadata.monthToTrigger || 1);
  const [selectedDay, setSelectedDay] = useState<number>(triggerMetadata.dayToTrigger || 1);
  const [customTriggerCopy, setCustomTriggerCopy] = useState(triggerMetadata.customTriggerCopy ?? '');
  const [selectedAudienceId, setSelectedAudienceId] = useState<string>(() =>
    getInitialAudienceId(state.localContents.includeSegmentDefaults),
  );
  const [excludedIds, setExcludedIds] = useState<string[]>(() =>
    getSegmentDefaultIds(excludeSegmentDefaults),
  );

  const handleDone = () => {
    const newTriggerMetadata = structuredClone(triggerMetadata);
    newTriggerMetadata.monthToTrigger = selectedMonth;
    newTriggerMetadata.dayToTrigger = selectedDay;
    newTriggerMetadata.customTriggerCopy = customTriggerCopy || undefined;
    updateTrigger(newTriggerMetadata);
    if (selectedAudienceId) {
      updateAudience([selectedAudienceId]);
    }
    const nextExclusions = getNextExclusions(excludeSegmentDefaults, excludedIds);
    if (nextExclusions) {
      updateExclusions(nextExclusions);
    }
    onClose();
  };

  const currentStepIndex = WIZARD_STEPS.indexOf(step);
  const { title, subtitle } = DATE_TRIGGER_STEP_COPY[step];

  return (
    <>
      <DialogBackdrop onClose={onClose} />
      <dialog className={styles.dialog} open>
        <header>
          <hgroup>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </hgroup>
          <button onClick={onClose} type="button" aria-label="Close">
            <X />
          </button>
        </header>

        <nav className={styles.stepper} aria-label="Setup progress">
          {WIZARD_STEPS.map((wizardStep, index) => (
            <React.Fragment key={wizardStep}>
              {index > 0 && (
                <span className={styles.stepperArrow} aria-hidden="true">
                  →
                </span>
              )}
              <span className={styles.stepperStep} data-state={getStepperState(index, currentStepIndex)}>
                {DATE_TRIGGER_STEP_COPY[wizardStep].stepperLabel}
              </span>
            </React.Fragment>
          ))}
        </nav>

        {step === 'date' ? (
          <DateTriggerDateSelectionStep
            selectedMonth={selectedMonth}
            selectedDay={selectedDay}
            customTriggerCopy={customTriggerCopy}
            onMonthChange={setSelectedMonth}
            onDayChange={setSelectedDay}
            onCustomTriggerCopyChange={setCustomTriggerCopy}
            onCancel={onClose}
            onContinue={() => setStep('audience')}
          />
        ) : (
          <DateTriggerAudienceSelectionStep
            selectedAudienceId={selectedAudienceId}
            onSelect={setSelectedAudienceId}
            excludedIds={excludedIds}
            onExcludedChange={setExcludedIds}
            onBack={() => setStep('date')}
            onCancel={onClose}
            onDone={handleDone}
          />
        )}
      </dialog>
    </>
  );
};
